import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Bell, 
  Mail, 
  Smartphone, 
  Moon, 
  Sun,
  Loader2,
  Save,
  BellRing,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [settings, setSettings] = useState({
    email_enabled: true,
    sms_enabled: false,
    push_enabled: false,
    email_address: '',
    phone_number: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush,
    sendTestNotification
  } = usePushNotifications();
  
  const [pushProcessing, setPushProcessing] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/notifications`, { withCredentials: true });
      setSettings({
        email_enabled: response.data.email_enabled ?? true,
        sms_enabled: response.data.sms_enabled ?? false,
        push_enabled: response.data.push_enabled ?? false,
        email_address: response.data.email_address || user?.email || '',
        phone_number: response.data.phone_number || ''
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings/notifications`, settings, { withCredentials: true });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleEnablePush = async () => {
    setPushProcessing(true);
    try {
      await subscribeToPush();
      setSettings(s => ({ ...s, push_enabled: true }));
      toast.success('Push notifications enabled! You will receive alerts on this device.');
    } catch (error) {
      if (error.message === 'Notification permission denied') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to enable push notifications');
      }
    } finally {
      setPushProcessing(false);
    }
  };

  const handleDisablePush = async () => {
    setPushProcessing(true);
    try {
      await unsubscribeFromPush();
      setSettings(s => ({ ...s, push_enabled: false }));
      toast.success('Push notifications disabled');
    } catch (error) {
      toast.error('Failed to disable push notifications');
    } finally {
      setPushProcessing(false);
    }
  };

  const handleTestPush = async () => {
    try {
      await sendTestNotification();
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error('Failed to send test notification. Make sure push is enabled.');
    }
  };

  const handleInstallPWA = () => {
    // Check if the PWA install prompt is available
    if (window.deferredPrompt) {
      window.deferredPrompt.prompt();
      window.deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          toast.success('App installed successfully!');
        }
        window.deferredPrompt = null;
      });
    } else {
      toast.info('To install: tap the share button and select "Add to Home Screen"');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight" data-testid="settings-page-title">
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your notification preferences and appearance
          </p>
        </motion.div>

        {/* Install App Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Download className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Install Alertafy App</h3>
                    <p className="text-sm text-muted-foreground">
                      Get instant push notifications on your device
                    </p>
                  </div>
                </div>
                <Button onClick={handleInstallPWA} className="rounded-full" data-testid="install-app-btn">
                  Install
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Push Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BellRing className="w-5 h-5 text-primary" />
                Push Notifications
                {pushSubscribed && (
                  <Badge variant="default" className="ml-2 bg-success">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Receive instant alerts directly on your device
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!pushSupported ? (
                <Alert>
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>
                    Push notifications are not supported in this browser. Try using Chrome, Firefox, or Edge.
                  </AlertDescription>
                </Alert>
              ) : pushPermission === 'denied' ? (
                <Alert variant="destructive">
                  <XCircle className="w-4 h-4" />
                  <AlertDescription>
                    Notifications are blocked. Please enable them in your browser settings to receive alerts.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <BellRing className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Push Notifications</p>
                        <p className="text-sm text-muted-foreground">
                          {pushSubscribed ? 'Enabled on this device' : 'Not enabled yet'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant={pushSubscribed ? 'outline' : 'default'}
                      onClick={pushSubscribed ? handleDisablePush : handleEnablePush}
                      disabled={pushProcessing || pushLoading}
                      className="rounded-full"
                      data-testid="push-toggle-btn"
                    >
                      {pushProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                      {!pushProcessing && (pushSubscribed ? 'Disable' : 'Enable')}
                    </Button>
                  </div>
                  
                  {pushSubscribed && (
                    <Button
                      variant="outline"
                      onClick={handleTestPush}
                      className="w-full rounded-full"
                      data-testid="test-push-btn"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Send Test Notification
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Appearance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                Appearance
              </CardTitle>
              <CardDescription>
                Customize how Alertafy looks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Dark Mode</p>
                  <p className="text-sm text-muted-foreground">
                    Toggle between light and dark themes
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Sun className="w-4 h-4 text-muted-foreground" />
                  <Switch 
                    checked={theme === 'dark'} 
                    onCheckedChange={toggleTheme}
                    data-testid="theme-switch"
                  />
                  <Moon className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Email & SMS Notification Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Email & SMS Notifications
              </CardTitle>
              <CardDescription>
                Configure additional notification channels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Notifications */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-muted-foreground">
                        Receive alerts via email
                      </p>
                    </div>
                  </div>
                  <Switch 
                    checked={settings.email_enabled}
                    onCheckedChange={(checked) => setSettings(s => ({ ...s, email_enabled: checked }))}
                    data-testid="email-notifications-toggle"
                  />
                </div>
                {settings.email_enabled && (
                  <div className="ml-13 space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={settings.email_address}
                      onChange={(e) => setSettings(s => ({ ...s, email_address: e.target.value }))}
                      placeholder="your@email.com"
                      data-testid="email-input"
                    />
                  </div>
                )}
              </div>

              <Separator />

              {/* SMS Notifications - via TextBolt */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">SMS Notifications</p>
                      <p className="text-sm text-muted-foreground">
                        Receive alerts via text message
                      </p>
                    </div>
                  </div>
                  <Switch 
                    checked={settings.sms_enabled}
                    onCheckedChange={(checked) => setSettings(s => ({ ...s, sms_enabled: checked }))}
                    data-testid="sms-notifications-toggle"
                  />
                </div>
                {settings.sms_enabled && (
                  <div className="ml-13 space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={settings.phone_number}
                      onChange={(e) => setSettings(s => ({ ...s, phone_number: e.target.value }))}
                      placeholder="+1 (555) 123-4567"
                      data-testid="phone-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter with country code (e.g., +1 for US)
                    </p>
                  </div>
                )}
              </div>

              <Separator />

              <Button 
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-full"
                data-testid="save-settings"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Account Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Account Information</CardTitle>
              <CardDescription>
                Your account details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{user?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Plan</p>
                  <p className="font-medium capitalize">{user?.plan || 'Free'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">User ID</p>
                  <p className="font-mono text-xs">{user?.user_id}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
